import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Plus, Search, ChevronRight } from 'lucide-react';
import { ComponentDefinition, COMPONENT_CATEGORIES } from '../../../core/types/inspector.types';
import { getAddableComponents } from '../../constants/components';
import { useSceneStore } from '../../store/sceneStore';
import { cn } from '../../utils/cn';

interface AddComponentDropdownProps {
  objectId: string;
}

export const AddComponentDropdown: React.FC<AddComponentDropdownProps> = ({ objectId }) => {
  const { addComponent, gameObjects } = useSceneStore();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const gameObject = gameObjects[objectId];
  const existingComponentTypes = useMemo(
    () => new Set(gameObject?.components.map((c) => c.type) || []),
    [gameObject?.components]
  );

  const addableComponents = useMemo(() => getAddableComponents(), []);

  // Group components by category
  const componentsByCategory = useMemo(() => {
    const grouped: Record<string, ComponentDefinition[]> = {};

    for (const component of addableComponents) {
      const category = component.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(component);
    }

    return grouped;
  }, [addableComponents]);

  // Filter components based on search
  const filteredComponents = useMemo(() => {
    if (!searchQuery.trim()) {
      return componentsByCategory;
    }

    const query = searchQuery.toLowerCase();
    const filtered: Record<string, ComponentDefinition[]> = {};

    for (const [category, components] of Object.entries(componentsByCategory)) {
      const matchingComponents = components.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.type.toLowerCase().includes(query)
      );
      if (matchingComponents.length > 0) {
        filtered[category] = matchingComponents;
      }
    }

    return filtered;
  }, [componentsByCategory, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        setExpandedCategory(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleAddComponent = useCallback(
    (definition: ComponentDefinition) => {
      // Create default data from property definitions
      const defaultData: Record<string, unknown> = {};
      for (const prop of definition.properties) {
        if (prop.defaultValue !== undefined) {
          defaultData[prop.name] = prop.defaultValue;
        }
      }

      addComponent(objectId, {
        id: Math.random().toString(36).substring(2, 11),
        type: definition.type,
        enabled: true,
        data: defaultData,
      });

      setIsOpen(false);
      setSearchQuery('');
      setExpandedCategory(null);
    },
    [objectId, addComponent]
  );

  const toggleDropdown = useCallback(() => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchQuery('');
      setExpandedCategory(null);
    }
  }, [isOpen]);

  const toggleCategory = useCallback((category: string) => {
    setExpandedCategory((prev) => (prev === category ? null : category));
  }, []);

  const categories = Object.keys(filteredComponents);

  return (
    <div ref={dropdownRef} className="relative px-3 py-3">
      {/* Add Component Button */}
      <button
        onClick={toggleDropdown}
        className={cn(
          'w-full flex items-center justify-center gap-2 px-4 py-2',
          'bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded',
          'text-sm text-zinc-300 hover:text-zinc-100 transition-colors'
        )}
      >
        <Plus size={16} />
        Add Component
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            'absolute left-3 right-3 mt-2 z-50',
            'bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl',
            'max-h-80 overflow-hidden flex flex-col'
          )}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-zinc-800">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500"
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search components..."
                className={cn(
                  'w-full pl-8 pr-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded',
                  'text-zinc-100 placeholder-zinc-500',
                  'focus:outline-none focus:border-blue-500'
                )}
              />
            </div>
          </div>

          {/* Component List */}
          <div className="overflow-y-auto flex-1">
            {categories.length === 0 ? (
              <div className="p-4 text-center text-xs text-zinc-500">
                No components found
              </div>
            ) : searchQuery ? (
              // Flat list when searching
              <div className="py-1">
                {Object.entries(filteredComponents).map(([category, components]) =>
                  components.map((component) => {
                    const isDisabled = existingComponentTypes.has(component.type);
                    return (
                      <button
                        key={component.type}
                        onClick={() => !isDisabled && handleAddComponent(component)}
                        disabled={isDisabled}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 text-left',
                          'text-xs text-zinc-300',
                          isDisabled
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:bg-zinc-800 cursor-pointer'
                        )}
                      >
                        <span>{component.icon}</span>
                        <span className="flex-1">{component.name}</span>
                        <span className="text-zinc-500 text-[10px]">{category}</span>
                      </button>
                    );
                  })
                )}
              </div>
            ) : (
              // Categorized list
              <div className="py-1">
                {categories.map((category) => (
                  <div key={category}>
                    {/* Category Header */}
                    <button
                      onClick={() => toggleCategory(category)}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-2 text-left',
                        'text-xs font-medium text-zinc-400',
                        'hover:bg-zinc-800/50 cursor-pointer'
                      )}
                    >
                      <ChevronRight
                        size={12}
                        className={cn(
                          'transition-transform',
                          expandedCategory === category && 'rotate-90'
                        )}
                      />
                      {category}
                      <span className="text-zinc-600 ml-auto">
                        {filteredComponents[category].length}
                      </span>
                    </button>

                    {/* Category Components */}
                    {expandedCategory === category && (
                      <div className="bg-zinc-800/30">
                        {filteredComponents[category].map((component) => {
                          const isDisabled = existingComponentTypes.has(component.type);
                          return (
                            <button
                              key={component.type}
                              onClick={() => !isDisabled && handleAddComponent(component)}
                              disabled={isDisabled}
                              className={cn(
                                'w-full flex items-center gap-2 pl-7 pr-3 py-2 text-left',
                                'text-xs text-zinc-300',
                                isDisabled
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'hover:bg-zinc-800 cursor-pointer'
                              )}
                            >
                              <span>{component.icon}</span>
                              <span>{component.name}</span>
                              {isDisabled && (
                                <span className="ml-auto text-[10px] text-zinc-500">
                                  (added)
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
